import { useAppDisplayModeContext } from '@archipro-design/aria';
import { Dialog } from '@archipro-design/aria';
import { useStyles } from '@archipro-design/aria';
import { useEffect, useState, useRef } from 'react';
import { useFetcher, useNavigate } from '@remix-run/react';
import { fetcherIsDone } from '~/utils/fetcherHelper';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { SubmitHandler, FieldValues } from 'react-hook-form';
import { DesignBoardDrawerHeader } from '@modules/design-board/component/right-side-drawer/DesignBoardRIghtSideDrawerHeader';
import { DesignBoardDrawerFooter } from '@modules/design-board/component/right-side-drawer/DesignBoardRightSideDrawerFooter';
import NewDesignBoardDrawerContent from '@modules/design-board/component/right-side-drawer/NewDesignBoardBody';
import { useDesignBoardPageState } from '@modules/design-board/component/context/DesignBoardContext';
import * as S from '@modules/design-board/component/right-side-drawer/RightSideNewBoarderDrawer.style';
import { DeleteDesignBoardDialog } from '@modules/design-board/component/right-side-drawer/DeleteDesignBoardDialog';
import type { NewBoardFormShape } from '@modules/design-board/validation/DesignBoardDetailPageSchemas';
import { NewBoardValidator } from '@modules/design-board/validation/DesignBoardDetailPageSchemas';
import { useToaster } from '~/modules/root/context/ToasterContext';
import { useSaveToDesignBoardContext } from '~/modules/design-board/component/context';
import type { RemoveInspirationMutation } from 'generated/graphql';
import { REMOVE_BUILD_HUB_INSPIRATION_API_ROUTE } from '~/routes/remix-api.app.build-hub-inspiration-remove';
import { CREATE_BUILD_HUB_INSPIRATION_API_ROUTE } from '~/routes/remix-api.app.build-hub-inspiration-create';
import {
    CREATE_AND_COPY_BUILD_HUB_INSPIRATION_API_ROUTE,
    type CreateAndCopyMoodBoardResponse,
} from '~/routes/remix-api.app.build-hub-inspiration-create-and-copy';
import {
    CREATE_AND_MOVE_BUILD_HUB_INSPIRATION_API_ROUTE,
    type CreateAndMoveMoodBoardResponse,
} from '~/routes/remix-api.app.build-hub-inspiration-create-and-move';

/** Response type for create/update/create-and-copy/create-and-move APIs */
type MoodBoardActionResponse =
    | CreateAndCopyMoodBoardResponse
    | CreateAndMoveMoodBoardResponse
    | { message: string; boardId?: number }
    | { projectInspirationGroupUpsert: { __typename: string; ID?: string } };

const DeleteDesignBoardConfirmation = (props: {
    open: boolean;
    onCancel: () => void;
    onDelete: () => void;
    loading: boolean;
}) => {
    const { css } = useStyles();
    return (
        <Dialog
            open={props.open}
            onCancel={props.onCancel}
            content={
                <DeleteDesignBoardDialog
                    onDelete={props.onDelete}
                    onCancel={props.onCancel}
                    loading={props.loading}
                />
            }
            className={css(S.DeleteDialog)}
            footer={null}
            header={null}
        />
    );
};

const MoodBoardDrawerEditMainContent = () => {
    const { dispatch, state } = useDesignBoardPageState();
    const navigate = useNavigate();
    const { toast } = useToaster();
    const { css } = useStyles();
    const { mobile } = useAppDisplayModeContext();

    const [isEditBoardLoading, setIsEditBoardLoading] = useState(false);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const saveToBoardContext = useSaveToDesignBoardContext();

    // fetchers
    const fetcher = useFetcher<MoodBoardActionResponse>();
    const deleteFetcher = useFetcher<RemoveInspirationMutation>();

    //refs
    const deletedBoardUrlSegmentRef = useRef<string | null>(null);
    const editingState = state.editMoodBoardInput;
    const creatingState = state.createMoodBoardInput;
    const isEditing = editingState !== undefined;

    // For moving/copying pins to newly created board
    const pinMoveData = state.pinMoveData;
    const pinAction = state.CreateBoardFromPinActionType;
    const hasPinsToMove =
        pinAction === 'MovePins' && !!pinMoveData?.pinListString;
    const hasPinsToCopy =
        pinAction === 'CopyPins' && !!pinMoveData?.pinListString;

    const {
        control,
        formState: { errors },
        handleSubmit,
        reset,
    } = useForm<NewBoardFormShape>({
        resolver: zodResolver(NewBoardValidator),
        mode: 'onBlur',
        ...(editingState && {
            defaultValues: editingState,
        }),
    });

    const onSubmit: SubmitHandler<FieldValues> = (data) => {
        setIsEditBoardLoading(true);

        // Edit mode
        if (editingState) {
            fetcher.submit(
                {
                    inspirationGroupId: editingState.id.toString(),
                    id: editingState.buildHubId.toString(),
                    title: data.title,
                    description: data.description || '',
                    isPrivate: data.isPrivate ? '1' : '0',
                },
                {
                    method: 'post',
                    action: CREATE_BUILD_HUB_INSPIRATION_API_ROUTE,
                }
            );
            return;
        }

        // Create mode
        if (!creatingState) return;

        // If we have pins to move or copy, use combined API
        if ((hasPinsToMove || hasPinsToCopy) && pinMoveData) {
            const action = hasPinsToMove
                ? CREATE_AND_MOVE_BUILD_HUB_INSPIRATION_API_ROUTE
                : CREATE_AND_COPY_BUILD_HUB_INSPIRATION_API_ROUTE;

            fetcher.submit(
                {
                    id: creatingState.buildHubId.toString(),
                    title: data.title,
                    description: data.description || '',
                    isPrivate: data.isPrivate ? '1' : '0',
                    sourceBoardId: pinMoveData.boardId.toString(),
                    pinListString: pinMoveData.pinListString,
                    buildHubUrl: pinMoveData.buildHubUrl || '',
                },
                {
                    method: 'post',
                    action,
                }
            );
        } else {
            // Just create board without copying pins
            fetcher.submit(
                {
                    id: creatingState.buildHubId.toString(),
                    title: data.title,
                    description: data.description || '',
                    isPrivate: data.isPrivate ? '1' : '0',
                },
                {
                    method: 'post',
                    action: CREATE_BUILD_HUB_INSPIRATION_API_ROUTE,
                }
            );
        }
    };

    const onDelete = () => {
        if (editingState) {
            deletedBoardUrlSegmentRef.current = editingState.buildHubUrl;
            deleteFetcher.submit(
                {
                    inspirationGroupId: editingState.id.toString(),
                    id: editingState.buildHubId.toString(),
                },
                {
                    method: 'post',
                    action: REMOVE_BUILD_HUB_INSPIRATION_API_ROUTE,
                }
            );
        }
    };

    // Clear board list cache after successful operations
    useEffect(() => {
        if (
            fetcherIsDone(fetcher) &&
            saveToBoardContext.state.myBoardList.length > 0
        ) {
            saveToBoardContext.dispatch({
                type: 'SetBoardPinData',
                payload: {
                    myBoardList: [],
                },
            });
        }
    }, [fetcher, saveToBoardContext]);

    // board deleted response - redirect back to project boards listing page
    useEffect(() => {
        const data = deleteFetcher.data?.projectInspirationGroupRemove;
        const isSuccess =
            deleteFetcher.state !== 'submitting' &&
            data?.__typename === 'InspirationGroup';

        if (isSuccess) {
            setIsDialogOpen(false);
            dispatch({ type: 'CloseDrawer' });
            reset();

            const urlSegment = deletedBoardUrlSegmentRef.current;
            if (urlSegment) {
                navigate(`/app/project/${urlSegment}/mood-boards`, {
                    replace: true,
                });
                deletedBoardUrlSegmentRef.current = null;
            }
        }
    }, [deleteFetcher.state, deleteFetcher.data, dispatch, reset, navigate]);

    // Handle fetcher response (create/update/create-and-copy)
    useEffect(() => {
        if (!isEditBoardLoading || !fetcherIsDone(fetcher)) return;

        const data = fetcher.data;
        if (!data) return;

        setIsEditBoardLoading(false);

        // Handle error response
        if (
            'message' in data &&
            !('boardId' in data && 'toastMessageExtra' in data)
        ) {
            toast(data.message, { type: 'error' });
            return;
        }

        // Handle combined API response (create-and-copy) - has toastMessageExtra
        if ('toastMessageExtra' in data) {
            const message = `New mood board "${data.title}" has been saved. ${data.toastMessageExtra}`;
            toast(message, { type: 'success' });
        }
        // Handle standard create/update response
        else if ('projectInspirationGroupUpsert' in data) {
            if (
                data.projectInspirationGroupUpsert?.__typename ===
                'InspirationGroup'
            ) {
                const message = isEditing
                    ? 'Mood board has been updated.'
                    : 'Mood board has been created.';
                toast(message, { type: 'success' });
            } else {
                toast('Failed to save mood board.', { type: 'error' });
                return;
            }
        }

        dispatch({ type: 'CloseDrawer' });
        reset();
    }, [fetcher, isEditBoardLoading, isEditing, toast, dispatch, reset]);

    const onClose = (_openValue: boolean = false) => {
        dispatch({
            type: 'CloseDrawer',
        });
        reset();
    };

    const modalClose = () => {
        setIsDialogOpen(false);
    };

    return (
        <form
            id="design-board-create-and-edit-form"
            className={css(S.FormClass)}
            onSubmit={handleSubmit(onSubmit)}
        >
            <DesignBoardDrawerHeader
                {...{ onClose }}
                headerTitle={
                    !isEditing
                        ? mobile
                            ? 'Create board'
                            : 'Create a board'
                        : 'Edit board'
                }
            />

            <NewDesignBoardDrawerContent control={control} errors={errors} />

            <DesignBoardDrawerFooter
                loading={isEditBoardLoading}
                {...(editingState
                    ? {
                          submitButtonLabel: 'SAVE',
                          buttonSecondary: {
                              onClick: () => setIsDialogOpen(true),
                              label: 'DELETE BOARD',
                          },
                      }
                    : {
                          submitButtonLabel: 'CREATE',
                      })}
            />
            <DeleteDesignBoardConfirmation
                open={isDialogOpen}
                onCancel={modalClose}
                onDelete={onDelete}
                loading={deleteFetcher.state !== 'idle'}
            />
        </form>
    );
};

export default MoodBoardDrawerEditMainContent;
